import { useEffect, useMemo, useRef, useState } from "react";
import { CaretDown, Check, House, Plus } from "@phosphor-icons/react";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Room picker Combobox for the asset form's `location` field.
 *
 * v3.15 replaces the free-form location text input with a controlled
 * vocabulary sourced from the active household's rooms list.
 *
 * v3.15.4 — each option now shows the room's hero photo (when set) as a
 * small square thumbnail so picking a room feels like a mini-tour of
 * the household rather than a raw string dropdown.
 *
 * Behaviour:
 *   • Shows household rooms as clickable options
 *   • Typing filters the list live
 *   • Typing a value that doesn't exist reveals "➕ Add 'Foo' as a new
 *     room" which POSTs to /households/{id}/rooms and selects it
 *   • Personal ledger (no household) falls back to a plain text input —
 *     rooms are a household-scoped feature
 *   • The rendered value stays in sync with `value` prop for controlled use
 */
export function RoomPicker({ value, onChange, testid = "form-room-picker" }) {
  const { activeLedgerId, households } = useAuth();
  const activeHousehold = useMemo(
    () => households.find((h) => h.id === activeLedgerId) || null,
    [households, activeLedgerId],
  );
  const householdId = activeHousehold?.id || null;

  const [rooms, setRooms] = useState([]);
  const [roomPhotos, setRoomPhotos] = useState({});
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || "");
  const [creating, setCreating] = useState(false);
  const ref = useRef(null);

  // Keep query in sync when parent controls value externally (edit flow)
  useEffect(() => { setQuery(value || ""); }, [value]);

  // Load rooms + hero photos for the active household
  useEffect(() => {
    if (!householdId) { setRooms([]); setRoomPhotos({}); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/households/${householdId}/rooms`);
        if (!cancelled) {
          setRooms(r.data?.rooms || []);
          setRoomPhotos(r.data?.room_photos || {});
        }
      } catch {
        if (!cancelled) { setRooms([]); setRoomPhotos({}); }
      }
    })();
    return () => { cancelled = true; };
  }, [householdId]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const q = (query || "").trim();
  const qLower = q.toLowerCase();
  const filtered = rooms.filter((r) => !qLower || r.toLowerCase().includes(qLower));
  const exactMatch = rooms.some((r) => r.toLowerCase() === qLower);
  const showAddRow = !!q && !exactMatch;

  const pick = (name) => {
    setQuery(name);
    onChange(name);
    setOpen(false);
  };

  const addNew = async () => {
    if (!q || !householdId) return;
    setCreating(true);
    try {
      const r = await api.post(`/households/${householdId}/rooms`, { name: q });
      setRooms(r.data?.rooms || []);
      setRoomPhotos(r.data?.room_photos || {});
      onChange(q);
      setOpen(false);
    } catch (e) {
      // Fall back to just selecting the string locally; the asset save
      // still succeeds because `location` is free-form on the backend.
      onChange(q);
      setOpen(false);
    } finally {
      setCreating(false);
    }
  };

  // Personal ledger — plain text input (rooms are household-only)
  if (!householdId) {
    return (
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        placeholder="e.g. Garage"
        className="w-full bg-white border border-[#E0DDD6] px-3 py-2 text-sm focus:outline-none focus:border-[#1A3622]"
      />
    );
  }

  return (
    <div ref={ref} className="relative" data-testid={testid}>
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            onChange(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && showAddRow) { e.preventDefault(); addNew(); }
            if (e.key === "Escape") setOpen(false);
          }}
          placeholder="Select or type a room…"
          data-testid={`${testid}-input`}
          className="w-full bg-white border border-[#E0DDD6] px-3 py-2 pr-8 text-sm focus:outline-none focus:border-[#1A3622]"
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setOpen((v) => !v)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-[#5C5F58] hover:text-[#0A192F]"
          aria-label="Toggle room picker"
        >
          <CaretDown size={14} />
        </button>
      </div>

      {open && (
        <div
          className="absolute left-0 right-0 mt-1 max-h-72 overflow-y-auto bg-white border border-[#1A3622] shadow-sm z-40"
          data-testid={`${testid}-menu`}
        >
          {filtered.length === 0 && !showAddRow && (
            <div className="px-3 py-3 text-xs text-[#5C5F58] italic">No rooms match.</div>
          )}
          {filtered.map((r) => {
            const selected = r === value;
            const photo = roomPhotos?.[r.toLowerCase()];
            return (
              <button
                type="button"
                key={r}
                onClick={() => pick(r)}
                data-testid={`${testid}-option-${r}`}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-[#F9F8F6] flex items-center gap-2 ${
                  selected ? "bg-[#F9F8F6]" : ""
                }`}
              >
                <div className="w-8 h-8 shrink-0 border border-[#E0DDD6] bg-[#F9F8F6] overflow-hidden flex items-center justify-center">
                  {photo ? (
                    <img
                      src={fileUrl(photo)}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ) : (
                    <House size={12} weight="regular" className="text-[#1A3622]" />
                  )}
                </div>
                <span className="flex-1 text-[#0A192F]">{r}</span>
                {selected && <Check size={12} weight="bold" className="text-[#1A3622]" />}
              </button>
            );
          })}
          {showAddRow && (
            <button
              type="button"
              onClick={addNew}
              disabled={creating}
              data-testid={`${testid}-add-new`}
              className="w-full text-left px-3 py-2 text-sm hover:bg-[#F9F8F6] flex items-center gap-2 border-t border-[#E0DDD6] bg-[#F9F8F6]/60 disabled:opacity-50"
            >
              <Plus size={12} weight="bold" className="text-[#1A3622] shrink-0" />
              <span className="flex-1 text-[#0A192F]">
                Add <span className="font-medium">&ldquo;{q}&rdquo;</span> as a new room
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default RoomPicker;
